import { z } from "zod";

import { CaseType } from "../case-core";
import { NoteSchema } from "../note";
import { BrazilBankingInfoSchema } from "./banking";
import { cnpjSchema, cpfSchema } from "./br-tax-id";
import { brazilPhoneSchema } from "./phone";

/** Roles allowed to receive client fields beyond identifiers and display names. */
export const CLIENT_RECORD_GROUPS = ["Admin", "Manager", "Automation"] as const;

export const ClientStatus = z.enum(["lead", "active", "in_negotiation", "expired", "archived"]);

export type ClientStatus = z.infer<typeof ClientStatus>;

export const ReachabilitySchema = z
  .object({
    phone1: brazilPhoneSchema,
    phone2: brazilPhoneSchema,
    email: z.email().optional(),
  })
  .describe("Phone and email contact details.");

export type Reachability = z.infer<typeof ReachabilitySchema>;

export const FleetType = z.enum(
  [
    "industrial_machinery_and_equipment",
    "buses",
    "utility_vehicles_and_vans",
    "two_wheeled_vehicles",
    "light_vehicles",
    "heavy_vehicles",
  ],
  { message: "clients_validation_fleet_type_required" },
);

export type FleetType = z.infer<typeof FleetType>;

export const FleetEntrySchema = z
  .object({
    type: FleetType,
    vehicleCount: z.coerce
      .number({ message: "clients_validation_vehicle_count_required" })
      .int({ message: "clients_validation_vehicle_count_positive" })
      .positive({ message: "clients_validation_vehicle_count_positive" }),
  })
  .describe("Client fleet composition entry.");

export type FleetEntry = z.infer<typeof FleetEntrySchema>;

const PaymentPercentageSchema = z
  .number({ message: "clients_validation_payment_percentage_range" })
  .min(0, { message: "clients_validation_payment_percentage_range" })
  .max(100, { message: "clients_validation_payment_percentage_range" });

const PaymentInstallmentsSchema = z
  .number({ message: "clients_validation_payment_installments_range" })
  .int({ message: "clients_validation_payment_installments_range" })
  .min(0, { message: "clients_validation_payment_installments_range" })
  .max(999, { message: "clients_validation_payment_installments_range" });

export const PaymentTermsSchema = z
  .object({
    upfront: z
      .object({
        maxDiscountPercent: PaymentPercentageSchema.optional(),
      })
      .optional(),
    interestFreeInstallments: z
      .object({
        maxInstallments: PaymentInstallmentsSchema.optional(),
      })
      .optional(),
    interestBearingInstallments: z
      .object({
        maxInstallments: PaymentInstallmentsSchema.optional(),
        monthlyInterestPercent: PaymentPercentageSchema.optional(),
        penaltyPercent: PaymentPercentageSchema.optional(),
        monetaryCorrectionPercent: PaymentPercentageSchema.optional(),
      })
      .optional(),
  })
  .describe(
    "Payment parameters for client negotiations; zero or absent installment limits disable that installment mode, while percentage values only configure their corresponding adjustment.",
  );

export type PaymentTerms = z.infer<typeof PaymentTermsSchema>;

export const clientMetadataFields = {
  id: z.uuid().meta({ visibleTo: "*" }),
  status: ClientStatus.meta({ visibleTo: CLIENT_RECORD_GROUPS }),
  createdAt: z.iso.datetime().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
  updatedAt: z.iso.datetime().meta({ visibleTo: CLIENT_RECORD_GROUPS }),
  createdBy: z.string().min(1).meta({ visibleTo: CLIENT_RECORD_GROUPS }),
  primaryDocumentContactUsername: z
    .string()
    .min(1)
    .nullish()
    .meta({ visibleTo: CLIENT_RECORD_GROUPS }),
};

export const updateCommonFields = {
  taxId: z.union([cpfSchema, cnpjSchema]).optional(),
  notes: z.array(NoteSchema).optional(),
  status: ClientStatus.optional(),
  primaryDocumentContactUsername: z.string().min(1).nullish(),
};

const PartnerSchema = z
  .object({
    validityPeriod: z.coerce
      .number()
      .int({ message: "clients_validation_validity_range" })
      .nonnegative({ message: "clients_validation_validity_range" })
      .optional(),
    commissionFee: z.coerce
      .number()
      .min(0, { message: "clients_validation_fee_range" })
      .max(100, { message: "clients_validation_fee_range" })
      .optional(),
    bankingInfo: BrazilBankingInfoSchema.optional(),
  })
  .describe("Partner details.");

export const ContractPhaseType = z.enum(["judicial", "extrajudicial"], {
  message: "clients_validation_contract_phase_type_invalid",
});
export type ContractPhaseType = z.infer<typeof ContractPhaseType>;

export const ContractPhaseSchema = z
  .object({
    type: ContractPhaseType,
    fee: z
      .number({ message: "clients_validation_contract_service_fee_required" })
      .min(0, { message: "clients_validation_fee_range" })
      .max(100, { message: "clients_validation_fee_range" })
      .describe("Agreed fee percentage for this phase."),
  })
  .describe("A contracted phase and its agreed fee percentage.");

const ContractCoverageSchema = z
  .object({
    caseType: CaseType,
    phases: z
      .array(ContractPhaseSchema)
      .min(1, { message: "clients_validation_contract_phases_required" })
      .refine((phases) => new Set(phases.map(({ type }) => type)).size === phases.length, {
        message: "clients_validation_contract_phases_duplicate",
      }),
  })
  .describe("Case type and the judicial phases covered by the contract.");

export const ContractSchema = z
  .object({
    startDate: z.iso.date({ message: "clients_validation_contract_start_date_required" }),
    durationMonths: z
      .number({ message: "clients_validation_contract_duration_range" })
      .int({ message: "clients_validation_contract_duration_range" })
      .positive({ message: "clients_validation_contract_duration_range" })
      .optional(),
    coverage: z
      .array(ContractCoverageSchema)
      .min(1, { message: "clients_validation_contract_coverage_required" })
      .refine(
        (coverage) => new Set(coverage.map(({ caseType }) => caseType)).size === coverage.length,
        {
          message: "clients_validation_contract_coverage_duplicate",
        },
      ),
    bankingInfo: BrazilBankingInfoSchema.optional(),
    partner: PartnerSchema.optional(),
  })
  .describe("Contract details.");

export type Contract = z.infer<typeof ContractSchema>;

export const availableCaseTypesForContract = (
  contract: Pick<Contract, "coverage"> | null | undefined,
) => contract?.coverage.map(({ caseType }) => caseType) ?? [];

export const contractCoversCaseType = (
  contract: Pick<Contract, "coverage"> | null | undefined,
  caseType: z.infer<typeof CaseType>,
): boolean => contract?.coverage.some((coverage) => coverage.caseType === caseType) ?? false;
